import { Platform } from 'react-native'
import Config from 'APP/Config'
import { ANDROID_API_VERSION } from 'APP/Config/constants'
import I18n from 'APP/I18n'
import * as Geolocation from 'expo-location'
import { all, put, call, select, spawn, delay, retry } from 'redux-saga/effects'
import { RESULTS as PermissionState, openSettings } from 'react-native-permissions'
import { PharmacyType } from '@dialogue/coredata'

import { navigationRef as Nav } from 'APP/Nav'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'

import Alert from 'APP/Converse/Alert'
// Redux Actions
import { familyActions } from 'APP/Store/Family'
import LoginActions, { selectAccessToken } from 'APP/Redux/LoginRedux'
import PatientActions, { selectPreferredLanguage } from 'APP/Redux/PatientRedux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import DeeplinkActions from 'APP/Redux/DeeplinkRedux'
import AppSessionActions from 'APP/Redux/AppSessionRedux'
import NotificationActions from 'APP/Redux/NotificationRedux'
import StartupActions from 'APP/Redux/StartupRedux'
import { getEpharmacyApi } from 'APP/Store/utils/healthConnect'
// Sagas
import {
  loginState,
  loginWithUsernameAndPassword,
  watchEmailVerificationCheckRequests,
  registerTokens,
  renewAuth,
} from './AuthSagas'
import { fetchEpisodes, fetchPractitioners, fetchAppointments } from './PatientHistorySagas'
import { deeplinkState } from 'APP/Sagas/DeeplinkSaga'
import { fetchNotificationPreferences } from './NotificationSagas'
import { familySagas } from 'APP/Store/Family'
import * as NavSagas from './NavSagas'
import Toast from 'react-native-toast-message'

// API Services
import authClient from 'APP/Services/Auth'
import CoreData from 'APP/Services/Coredata'
import Waldo from 'APP/Services/Waldo'
import Mattermost from 'APP/Services/Mattermost'
import Scribe from 'APP/Services/Scribe'
import Silkroad from 'APP/Services/Silkroad'
import Analytics, { LAUNCH_CATEGORY } from 'APP/Services/Analytics'
import { identifyLD } from 'APP/Services/LaunchDarkly'
import EnrolmentChallengeOperations from 'APP/Helpers/EnrolmentChallengeOperations'
import sprigClient from 'APP/Services/Sprig'

// Utils
import { buildSMSPreference } from 'APP/Lib/PreferenceHelpers'
import { getShouldProvideProvince } from 'APP/Lib/ProfileHelpers'
import { handleNonRetryableRequestFailure } from 'APP/Lib/SagaRequestFailureHandler'
import {
  checkLocationState,
  checkLocationService,
  requestLocation,
  requestLocationService,
  checkPostNotificationsState,
  requestPostNotificationsPermissions,
  checkCanUseFullScreenIntent,
} from 'APP/NativeModules/PermissionsService'
import PushNotification from 'react-native-push-notification'
import { isMobile, isAndroid, isIOS, meetsMinimumVersion, isWeb } from 'APP/Helpers/checkPlatform'
import { handlePressContactSupport } from 'APP/Helpers/handlePressContactSupport'
import { PublicComputerType, VerifyEmailErrors } from 'APP/Lib/Enums'
import { clearAllQueryParamsAndHash } from 'APP/Lib/Utilities'
import { contentSelectors, contentSagas } from 'APP/Store/Content'
import { activeMinutesLogin } from './ActiveMinutesSagas'
import { logger, trackDdEvent } from 'APP/Lib/Datadog'
import { REGISTRATION_ACTIONS } from 'APP/Lib/Datadog/constants'
import { pharmaciesActions } from 'APP/Store/Pharmacies'
import Regex from 'APP/Lib/Regex'
import { getUsersApi } from 'APP/Store/utils/multipass'
import { isVideoCallRoute, waitForCallEnd } from 'APP/Store/VideoSession/sagas'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'
import Assets from 'APP/Services/Assets'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { shouldShowAccountLinking } from 'APP/Lib/AccountLinkingHelpers'
import { canEnrolInBrand, isCrossBrandListed } from 'APP/Lib/BrandHelpers'
import {
  acquireLocationUpdateGate,
  releaseLocationUpdateGate,
} from 'APP/BrownfieldBridge/locationUpdateGate'
import { consumeDestination, clearPendingManualConnection } from 'APP/Lib/SeamlessDestination'

// exported to make available for tests
export const fullState = (state) => state

export const cobrandingsState = (state) => state.cobrandings
export const patientState = (state) => state.patient
export const patientHistoryState = (state) => state.history
export const patientProfileState = (state) => state.patient.profile
export const appState = (state) => state.app
export const userSettingsState = (userId) => (state) =>
  (state.app && state.app.userSettings && state.app.userSettings[userId]) || {}
export const basicProfileState = (state) => {
  return {
    givenName: state.patient.profile.givenName,
    familyName: state.patient.profile.familyName,
    dateOfBirth: state.patient.profile.dateOfBirth,
    adminAreaISOCode: state.patient.profile.adminAreaISOCode,
    phoneNumber: state.patient.profile.phoneNumber,
    preferredLanguage: state.patient.profile.preferred_language,
    profileSetupStatus: state.patient.profile.profileSetupStatus,
  }
}

export const isCommitmentsRequiredState = (state) => {
  const patient = patientState(state)

  const includesRequiredService =
    patient.profile?.eligibleServices &&
    Object.keys(patient.profile?.eligibleServices)?.some(
      (service) => Config.COMMITMENT.requiredServices.indexOf(service) >= 0
    )

  return patient?.displayCommitmentScreen && includesRequiredService
}

const hasActiveEligibility = (patient) =>
  patient?.profile?.eligibilityIntervals?.some((interval) => interval.status === 'active')

export const isBasicProfileComplete = (profile) => {
  const shouldProvideProvince = getShouldProvideProvince()

  return (
    profile &&
    profile.givenName &&
    profile.familyName &&
    profile.dateOfBirth &&
    (!shouldProvideProvince || profile.adminAreaISOCode) &&
    profile.phoneNumber &&
    profile.preferredLanguage
  )
}

export const incomingCallEncounters = (state) => {
  const encounters = state.history.encounters
  const encounterIds = Object.keys(encounters)
  let returnArray = []

  encounterIds.forEach((key) => {
    const encounter = encounters[key]
    if (encounter && encounter.status === 'IN_PROGRESS') returnArray.push(encounter)
  })

  return returnArray
}

export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    Geolocation.getCurrentPositionAsync({
      //Balanced and lowest didn't fetch the location ("Location provider is unavailable" error)
      accuracy: Geolocation.Accuracy.High,
    }).then(
      (position) => {
        const { latitude, longitude } = position.coords
        resolve({
          latitude,
          longitude,
        })
      },
      (error) => {
        let errorCode
        try {
          errorCode = Object.keys(error)
            .filter((k) => {
              return k !== 'code' && error[k] === error.code
            })
            .pop()
        } catch (_e) {
          errorCode = 'UNDEFINED'
        }

        const message = (error && error.message) || 'UNDEFINED'

        reject({
          error: errorCode,
          message,
        })
      }
    )
  })
}

const CONSENT_CHECK_RETRIES = 2
const CONSENT_CHECK_RETRY_DELAY_MS = 1000
const UNKNOWN_EMAIL_VERIFICATION_STATUS_ERROR_MESSAGE =
  'Unable to determine email verification status'

function* getConsentContext() {
  const login = yield select(loginState)
  const language_code = yield select(selectPreferredLanguage)
  const coredataClient = CoreData.create(login.accessToken)
  const assetsClient = Assets.create(Config.ASSETS_DOMAIN)
  const country = assetsClient.getConsentFormRegion()
  const brandId =
    Config.BRAND_ID && Config.BRAND_VERSION
      ? `${Config.BRAND_ID}_${Config.BRAND_VERSION}`
      : Config.BRAND_ID
  return { coredataClient, assetsClient, country, brandId, language_code }
}

export function* waitIfOnVideoCallRoute(sagaName) {
  logger.info(`${sagaName} saga checking for video route`, {
    currentRoute: Nav.getCurrentRoute()?.name,
  })
  if (isVideoCallRoute()) {
    logger.info(
      `${sagaName} saga started while on video call route. Waiting for call to end before continuing.`
    )
    yield call(waitForCallEnd)
    logger.info(`Call ended. Continuing with ${sagaName} saga.`)
  }
}

export function* requestEpisodes() {
  try {
    const { enableNewEpisodeFetch } = yield select(selectFeatures)
    // The bootstrap fetch returns the episodes alongside the channels, so there is no
    // separate episode fetch to make. TEMP: drop the branch with the flag.
    if (!enableNewEpisodeFetch) {
      yield call(fetchEpisodes)
    }
    yield put(PatientHistoryActions.fetchChannelsRequest(true))
    yield put(PatientHistoryActions.cleanChannels())
    yield spawn(updateLocation)
  } catch (e) {
    yield put(PatientActions.patientProfileFetchFailure(e))
  }
}

export function* requestMessagingCredentials(retryCount = 0) {
  logger.info('requestMessagingCredentials saga started', { retryCount })
  const state = yield select(fullState)

  const mattermost = Mattermost.create(state.login.accessToken)
  try {
    const response = yield call(mattermost.getMe)

    if (!response?.props?.team_id) {
      throw new Error('team_id not defined in Mattermost.getMe response')
    }

    yield put(LoginActions.setMessagingCredentials(response))
  } catch (e) {
    // Note: The retry here is to account for mattermost not being updated
    // synchronously via chainserv - It should eventually be updated with
    // the new user, allowing us to get the new user info from MM. See
    // DIA-87642
    if (retryCount < 10) {
      yield delay(1500)
      yield call(requestMessagingCredentials, ++retryCount)
    } else {
      const hasToken = !!state.login.accessToken
      const errorMessage = `Failed requesting mattermost messaging credentials. Token was set: ${hasToken}, Error Msg: ${e?.message}`
      const isEmbedded = isEmbeddedEnvironment()

      logger.error(
        'Mattermost credential retries exhausted',
        { hasToken, errorMessage: e?.message, isEmbedded },
        e
      )

      /*
       * Embedded mode (Brownfield host, e.g. Sun Life mySL): the host's bridge handler
       * treats a LOGOUT as a popToNative trigger and dismisses the embedded view.
       * Messaging is not a precondition for using Dialogue here — appointments, video
       * calls and content all work without it — so a transient Mattermost 401 must not
       * pop the user back to the host. Soft-fail and let messaging retry on the next
       * foreground / checkRenewAuth tick. Standalone (Dialogue / Sun Life standalone /
       * any other brand) keeps its existing fatal-logout behavior below.
       */
      if (isEmbedded) {
        yield put(AppSessionActions.setSystemError(errorMessage, false))
        return
      }

      yield put(AppSessionActions.setSystemError(errorMessage, true))
      yield put(LoginActions.logout(errorMessage))
      yield call(Nav.reset, { index: 0, routes: [{ name: 'welcome' }] })

      Toast.show({
        text1: I18n.t('WelcomeScreen.errorTitle'),
        text2: I18n.t('WelcomeScreen.errorSubtitle'),
      })

      throw new Error(errorMessage)
    }
  }
}

// this block around location permission is messy and spread out accross 2 sagas
// It's a cause of a P2 issue https://dialoguemd.atlassian.net/browse/DIA-75867
// To unblock members it has been modified to skip the check alltogether if we encounter any error
export function* requestLocationPermission({ skip }) {
  try {
    if (!skip) {
      const result = yield call(requestLocation)
      if (Platform.OS === 'android' && (result === 'denied' || result === 'blocked')) {
        yield call(navigateWithLocationPermission, false)
        return false
      } else {
        yield spawn(updateLocation)
      }
    }
    yield call(navigateWithLocationPermission)
  } catch (error) {
    logger.error('Failed to request location permission', {}, error)
    // skip any remaining location checks for Android and advance with the rest of onboarding
    yield call(navigateWithLocationPermission, false)
  }
}

export function* redirectToLocationService({ skip }) {
  if (Platform.OS === 'android') {
    if (!skip) {
      yield requestLocationService()
      yield spawn(updateLocation)
    }
    yield call(navigateWithLocationPermission, !skip)
  } else {
    yield call(navigateWithLocationPermission)
  }
}

export function* updateLocationAuthorized() {
  let updated = false
  try {
    const data = yield call(getCurrentPosition)

    const updatedLocation = yield call(refreshWaldoLocation, data)
    if (updatedLocation) {
      const province =
        updatedLocation.administrativeAreaLevel1 ||
        (updatedLocation.Attributes && updatedLocation.Attributes.administrativeAreaLevel1)

      //ATTN: Keep both levels to extract provinces, response wrapped in certain cases
      yield put(
        PatientActions.patientUpdateLocationSuccess({
          longitude: data && data.longitude,
          latitude: data && data.latitude,
          province,
        })
      )
    } else {
      yield put(PatientActions.patientUpdateLocationFailure('invalid geolocation'))
    }
    updated = true
  } catch (_error) {}
  return updated
}

export function* updateLocation() {
  const locationPermission = yield call(checkLocationState)

  let shouldUpdateLocation
  if (Platform.OS === 'android') {
    const systemLocation = yield call(checkLocationService)
    shouldUpdateLocation = locationPermission === 'granted' && systemLocation
  } else {
    shouldUpdateLocation = locationPermission === 'granted'
  }

  const locationUpdated = shouldUpdateLocation ? yield call(updateLocationAuthorized) : false

  if (!locationUpdated) {
    //ATTN: updatedLocation is updated in case of error ^^
    let msg = 'not shared'
    try {
      yield call(refreshWaldoLocation)
    } catch (error) {
      msg = error
    }
    yield put(PatientActions.patientUpdateLocationFailure(msg))
  }
}

export function* refreshWaldoLocation(data) {
  const login = yield select(loginState)
  const waldo = Waldo.create(login.accessToken)
  const patient = yield select(patientState)
  if (waldo) {
    if (data !== undefined) {
      const response = yield call(waldo.setUserLocation, patient.profile.id, data)
      return response
    } else {
      const response = yield call(waldo.setUserLocationNotShared, patient.profile.id)
      return response
    }
  }
  return false
}

export function* fetchUserLocation() {
  const login = yield select(loginState)
  const waldo = Waldo.create(login.accessToken)
  const patient = yield select(patientState)
  if (waldo) {
    try {
      const response = yield call(waldo.getUserLocation, patient.profile.id)
      return response?.[0] ?? null
    } catch {
      return null
    }
  }
  return null
}

export function* fetchPatientProfile() {
  const { accessToken } = yield select(loginState)
  const coredataClient = CoreData.create(accessToken)
  try {
    // attempts to get the patient
    const [response, encountersResponse] = yield all([
      call(coredataClient.getPatient),
      call(coredataClient.getPatientEncounters),
    ])

    const patientProfile = response?.data?.data // TODO: consider API transform utility
    const encounters = encountersResponse?.data?.data // TODO: consider API transform utility
    if (encounters) {
      yield put(PatientHistoryActions.fetchEncountersSuccess(encounters))
    }
    if (patientProfile) {
      yield put(PatientActions.patientProfileFetchSuccess(patientProfile))
      if (patientProfile.id) {
        // We send an hashed version of the user_id to sprig
        sprigClient.setUserIdentifier(patientProfile.id)
      }
    }
  } catch (e) {
    const tokenStatus = !!accessToken
    const error = new Error(
      `Failed fetching patient: ${e?.message}. Token status: ${tokenStatus ? 'truthy' : 'falsy'}`
    )
    yield put(PatientActions.patientProfileFetchFailure(error.message))
    yield put(AppSessionActions.setSystemError(error.message, true))
    yield put(LoginActions.logout(error.message))

    if (!isEmbeddedEnvironment()) {
      yield call(Nav.reset, { index: 0, routes: [{ name: 'welcome' }] })
    }
    Toast.show({
      text1: I18n.t('WelcomeScreen.errorTitle'),
      text2: I18n.t('WelcomeScreen.errorSubtitle'),
    })
    throw error
  }
}

export function* fetchMedicalIdUrl() {
  const login = yield select(loginState)
  const coredataClient = CoreData.create(login.accessToken)
  const medicalIDResponse = yield call(coredataClient.getPatientMedicalIdURL)
  if (medicalIDResponse.ok) {
    yield put(PatientActions.patientProfileMedicalIdFetchSuccess(medicalIDResponse.data.data.url))
  } else {
    yield put(PatientActions.patientProfileMedicalIdFetchFailure(medicalIDResponse))
  }
}

const CONSENT_ERROR_TOAST = {
  text1: I18n.t('ConsentScreen.error.post.title'),
  text2: I18n.t('ConsentScreen.error.post.message'),
  visibilityTime: 4000,
}

export function* patientProfileGiveConsent({ forms }) {
  const { coredataClient, assetsClient, country, brandId, language_code } = yield call(
    getConsentContext
  )

  const uniqueConsentForms = new Map()
  const consentsToPost = []

  for (const form of forms) {
    const type = form.member.type === 'child' ? 'guardian' : 'patient'

    if (uniqueConsentForms.has(type) || form.state !== 'accept') {
      continue
    }

    uniqueConsentForms.set(type, form)

    const version = yield call(
      assetsClient.getConsentFormVersion,
      brandId,
      country,
      language_code,
      type
    )

    if (!version) {
      logger.error('Error getting consent version - response not ok', {
        country,
        language_code,
        type,
        version,
      })
      Toast.show(CONSENT_ERROR_TOAST)
      yield put(
        PatientActions.patientProfileGiveConsentFailure({
          error: 'CONSENT_VERSION_MISSING',
          message: 'Failed to retrieve consent form version',
        })
      )
      return
    }

    consentsToPost.push({
      consent_type: type,
      consent_brand: brandId,
      version: version,
      language_iso_code: language_code,
      country_iso_code: country,
    })
  }

  try {
    const response = yield call(coredataClient.updatePatient, {
      hasConsented: true,
      consents: consentsToPost,
    })
    if (response.ok) {
      const patientProfile = response.data.data
      yield put(PatientActions.patientProfileGiveConsentSuccess(patientProfile))
      yield call(trackDdEvent, REGISTRATION_ACTIONS.consentAccepted)
      yield delay(500)
      yield call(navigateWithConsent)
    } else {
      logger.error('Error giving patient consent - response not ok', {
        problem: response?.problem,
        status: response?.status,
      })
      Toast.show(CONSENT_ERROR_TOAST)
      yield put(PatientActions.patientProfileGiveConsentFailure(response))
    }
  } catch (error) {
    logger.error('Error giving patient consent - exception caught', {}, error)
    Toast.show(CONSENT_ERROR_TOAST)
    yield put(PatientActions.patientProfileGiveConsentFailure(error))
  }
}

/**
 * Fetches all startup data (user info, profile, scribe, messaging, etc.)
 */
export function* fetchStartupData() {
  const { accessToken } = yield select(loginState)

  const userInfoResult = yield call(fetchUserInfo, accessToken)

  if (!userInfoResult.ok) {
    return userInfoResult
  }

  const emailVerified = userInfoResult.userInfo?.email_verified

  if (emailVerified === false) {
    return { ok: true, emailVerified: false }
  }

  if (emailVerified !== true) {
    return { ok: false, error: new Error(UNKNOWN_EMAIL_VERIFICATION_STATUS_ERROR_MESSAGE) }
  }

  if (emailVerified) {
    yield call(fetchPatientProfile)
    yield spawn(Analytics.trackLoadingTime, {
      action: 'patient profile fetched',
    })
    const patient = yield select(patientState)

    yield call(Analytics.identify, patient.profile.id)
    yield call(identifyLD, patient.profile.id)

    yield spawn(Analytics.trackLoadingTime, {
      action: 'email verification checked',
    })

    yield all([call(fetchScribeV2User), call(requestMessagingCredentials), call(registerTokens)])
    // Get missing profile data
    // done in a sequence as fetchProfileData relies on the result of fetchScribeV2User
    // to retrieve patient.profile.contentTags
    yield call(fetchProfileData)

    yield spawn(fetchAppointments)

    // This depends on the fetchProfileData to have retrieved the service features
    yield spawn(activeMinutesLogin)

    yield spawn(Analytics.trackLoadingTime, {
      action: 'Scribe, practitioners and location updated',
    })

    return { ok: true, emailVerified: true }
  }
}

export function* navigateWithEmailVerificationCheck() {
  const startupResult = yield call(fetchStartupData)

  if (!startupResult.ok) {
    logger.error(UNKNOWN_EMAIL_VERIFICATION_STATUS_ERROR_MESSAGE, {}, startupResult.error)
    yield call(Nav.reset, {
      index: 0,
      routes: [{ name: 'startupServiceAuthenticationError' }],
    })
    return
  }

  const emailVerified = startupResult.emailVerified ?? false

  if (!emailVerified) {
    yield put(PatientActions.setIsOnboarding(true))
    yield spawn(Analytics.trackLoadingTime, {
      action: LAUNCH_CATEGORY.EXIT,
      label: 'verify email',
    })
    yield call(Nav.reset, { index: 0, routes: [{ name: 'verifyEmail' }] })
    yield spawn(watchEmailVerificationCheckRequests)
  } else {
    yield call(navigateWithEmailVerified)
  }
}

export function* startupNavigation() {
  yield put(LoginActions.setIsLoggingIn(false))

  const needsAccountLinking = yield select(shouldShowAccountLinking)
  if (needsAccountLinking) {
    // Embedded relies on AccountLinkingGuard to navigate once the native shell
    // mounts the JS tree; standalone must navigate here.
    if (!isEmbeddedEnvironment()) {
      yield call(Nav.reset, { index: 0, routes: [{ name: 'accountLinking' }] })
    }
    return
  }

  yield call(navigateWithEmailVerificationCheck)
}

export function* fetchMembersExternalClaims() {
  const patient = yield select(patientState)

  try {
    const api = yield call(getUsersApi)

    const response = yield call(api.userExternalClaimsUsersAuthIdExternalClaimsGet, {
      authId: patient.profile.authId,
    })

    yield put(PatientActions.getExternalClaimsSuccess(response?.data?.data))
  } catch (error) {
    logger.error('Error fetching user external claims', {}, error)

    // Swallow error
    return null
  }
}

export function* fetchProfileData() {
  const patient = yield select(patientState)

  yield all([
    patient.profile.authId && call(fetchMembersExternalClaims),
    call(fetchScribeV2UserEligibilities),
    call(requestEpisodes),
    spawn(familySagas.fetchFamily),
    spawn(fetchPractitioners),
    spawn(updateLocation),
    patient.profile.idCard && spawn(fetchMedicalIdUrl),
    spawn(contentSagas.fetchLayout, { contentTags: patient.profile.contentTags }),
    call(contentSagas.fetchServiceFeatures),
    call(fetchNotificationPreferences),
    spawn(contentSagas.fetchServiceGroups),
  ])
}

function* attemptConsentStatusCheck(patient) {
  const { coredataClient, assetsClient, country, brandId, language_code } = yield call(
    getConsentContext
  )

  const patientConsentVersion = yield call(
    assetsClient.getConsentFormVersion,
    brandId,
    country,
    language_code,
    'patient'
  )

  const consentResponse = yield call(coredataClient.getConsentStatus, {
    patientId: patient.profile.id,
    consentBrand: brandId,
    patientConsentVersion,
  })

  if (!consentResponse.ok) {
    logger.error('Consent status API failed', {
      patientId: patient.profile.id,
      consentBrand: brandId,
      problem: consentResponse.problem,
      status: consentResponse.status,
    })
    throw new Error(`Consent status API failed: ${consentResponse.problem}`)
  }

  return consentResponse.data?.data?.patient
}

export function* checkConsentStatusWithRetry(patient) {
  yield put(PatientActions.checkConsentStatusRequest())

  try {
    const hasCurrentConsentVersion = yield retry(
      CONSENT_CHECK_RETRIES,
      CONSENT_CHECK_RETRY_DELAY_MS,
      attemptConsentStatusCheck,
      patient
    )
    yield put(PatientActions.checkConsentStatusSuccess(hasCurrentConsentVersion))
    return { success: true, hasCurrentConsentVersion }
  } catch (error) {
    yield put(PatientActions.checkConsentStatusFailure(error.message))

    logger.error('Error checking consent status', {}, error)

    return { success: false, error: error.message }
  }
}

export function* navigateWithEmailVerified() {
  const patient = yield select(patientState)
  const basicProfile = yield select(basicProfileState)
  const flow = !isBasicProfileComplete(basicProfile) ? 'signupFlow' : 'startupFlow'

  yield put(familyActions.fetchFamilyRequest())

  const consentResult = yield call(checkConsentStatusWithRetry, patient)

  if (!consentResult.success) {
    yield spawn(Analytics.trackLoadingTime, {
      action: LAUNCH_CATEGORY.EXIT,
      label: `consent check failed - show connection error: ${consentResult.error}`,
    })
    yield put(PatientActions.setIsOnboarding(true))
    yield call(Nav.reset, { index: 0, routes: [{ name: 'consent', params: { flow } }] })
  } else if (!consentResult.hasCurrentConsentVersion) {
    yield spawn(Analytics.trackLoadingTime, {
      action: LAUNCH_CATEGORY.EXIT,
      label: 'complete consent',
    })
    yield put(PatientActions.setIsOnboarding(true))
    yield call(Nav.reset, { index: 0, routes: [{ name: 'consent', params: { flow } }] })
  } else {
    yield spawn(Analytics.trackLoadingTime, {
      action: 'consent complete',
    })
    yield call(navigateWithConsent)
  }
}

// Basic profile check
export function* navigateWithConsent() {
  const basicProfile = yield select(basicProfileState)
  // profileSetupStatus is set in CoreData when profile information is prefilled
  // before the user signs up and needs to be confirmed during onboarding
  if (!isBasicProfileComplete(basicProfile) || basicProfile.profileSetupStatus === 'prefilled') {
    yield spawn(Analytics.trackLoadingTime, {
      action: LAUNCH_CATEGORY.EXIT,
      label: 'complete profile',
    })
    yield put(PatientActions.setIsOnboarding(true))
    yield call(Nav.reset, { index: 0, routes: [{ name: 'newProfile' }] })
  } else {
    yield spawn(Analytics.trackLoadingTime, {
      action: 'profile complete',
    })
    yield call(navigateWithBasicProfile)
  }
}

// Attempt to defer enrol during onboarding (first time sign up only)
// Else, geolocation check and request
export function* navigateWithBasicProfile() {
  logger.info('navigateWithBasicProfile saga started')
  yield call(waitIfOnVideoCallRoute, 'navigateWithBasicProfile')

  const patient = yield select(patientState)
  // minimum onboarding done (profile), we can accept authenticated deeplinks now
  yield put(DeeplinkActions.setReadyToProcess(true))
  if (patient.isOnboarding && !hasActiveEligibility(patient)) {
    yield call(Nav.navigate, 'loading', { hideLogo: true })
    yield call(unchallengedInvitationSearch)
  } else {
    yield call(navigateToCommitmentScreen)
  }
}

export function* navigateToGeolocationCheck() {
  try {
    logger.info('navigateToGeolocationCheck saga started')

    yield call(waitIfOnVideoCallRoute, 'navigateToGeolocationCheck')

    const serviceFeatures = yield select(contentSelectors.selectServiceFeatures)
    const geolocationIsNeeded = serviceFeatures.geolocation
    const locationPermission = yield call(checkLocationState)
    const locationPermissionsDenied = locationPermission === 'denied'
    const shouldRequestLocation = geolocationIsNeeded && locationPermissionsDenied

    if (shouldRequestLocation) {
      yield spawn(Analytics.trackLoadingTime, {
        action: LAUNCH_CATEGORY.EXIT,
        label: 'request geolocation',
      })
      acquireLocationUpdateGate()
      yield call(Nav.reset, { index: 0, routes: [{ name: 'newGeolocation' }] })
    } else {
      yield spawn(Analytics.trackLoadingTime, {
        action: !locationPermissionsDenied ? 'geolocation granted' : 'geolocation skipped',
      })

      if (Platform.OS === 'android') {
        // If location permission screen is not prompted, do not display location service screen
        yield call(navigateWithLocationPermission, false)
      } else {
        yield call(navigateWithLocationPermission)
      }
    }
  } catch (error) {
    logger.error('Failed to navigate through geolocation check', {}, error)
  }
}

export function* navigateToCommitmentScreen() {
  const isCommitmentsRequired = yield select(isCommitmentsRequiredState)

  if (isCommitmentsRequired) {
    yield call(Nav.navigate, 'commitment')
  } else {
    yield call(navigateToGeolocationCheck)
    yield put(PatientActions.setDisplayCommitmentScreen(false))
  }
}

export function* navigateWithLocationPermission(enableCheck = true) {
  releaseLocationUpdateGate()
  try {
    logger.info('navigateWithLocationPermission saga started')
    // We need to avoid navigating during a video call since it would end it.
    yield call(waitIfOnVideoCallRoute, 'navigateWithLocationPermission')

    if (Nav.getCurrentRoute()?.name === 'updateGeolocation') {
      yield call(NestedNavHelper.pop)
    } else {
      //Check location service - skip location service screen if permission was skipped
      let locationServiceEnabled = true
      if (Platform.OS === 'android' && enableCheck) {
        locationServiceEnabled = yield call(checkLocationService)
      }

      if (locationServiceEnabled) {
        const pushPermissionState = yield call(checkPostNotificationsState)
        const { pushPermissionsAccepted } = yield select(appState)
        // Hack to bypass navigation issues on embedded apps
        // TODO: Configure proper navigation flow for embedded apps DIA-85526
        const shouldSkipNotificationScreen =
          isEmbeddedEnvironment() && Config.EMBEDDED_SKIP_NOTIFICATION_SCREEN
        if (shouldSkipNotificationScreen) {
          yield call(navigateToPublicComputer)
          return
        }

        // The screen's CTA is a no-op on Android < 13 (POST_NOTIFICATIONS is not a
        // runtime permission there), so there's no point showing it.
        const supportsPushPermissionScreen =
          !isAndroid() || meetsMinimumVersion(ANDROID_API_VERSION.ANDROID_13)

        // Don't re-show the permission screen if the user has already responded —
        // on Android, checkNotifications() never returns BLOCKED, so we need this flag.
        if (
          supportsPushPermissionScreen &&
          pushPermissionState !== PermissionState.GRANTED &&
          pushPermissionState !== PermissionState.BLOCKED &&
          pushPermissionsAccepted === null
        ) {
          yield spawn(Analytics.trackLoadingTime, {
            action: LAUNCH_CATEGORY.EXIT,
            label: 'push notification permission',
          })
          yield call(Nav.reset, { index: 0, routes: [{ name: 'pushNotificationPermission' }] })
        } else {
          // On Android this doesn't actually request permissions but calls getToken
          // which on success envokes onRegistration "forcing" a registration event
          PushNotification.requestPermissions()

          // Web users never see the "allow notifications" screen, so default SMS
          // appointment reminders to enabled on account creation on web.
          const patient = yield select(patientState)
          if (isWeb() && patient.isOnboarding) {
            const sms = buildSMSPreference(true, true)
            yield put(NotificationActions.updateNotificationPreference(sms))
          }

          yield call(navigateToPublicComputer)
        }
      } else {
        yield spawn(Analytics.trackLoadingTime, {
          action: LAUNCH_CATEGORY.EXIT,
          label: 'request system geolocation',
        })
        yield call(Nav.reset, { index: 0, routes: [{ name: 'geolocationService' }] })
      }
    }
  } catch (error) {
    logger.error('Failed to navigate with location permission', {}, error)
  }
}

export function* requestPostNotifications() {
  let granted = false

  if (isIOS()) {
    try {
      const res = yield call([PushNotification, 'requestPermissions'])
      granted = !!res?.alert
    } catch (e) {
      logger.error('Permission request failed', {}, e)
      // Early return lets the notification screen reappear on next launch
      return
    }
  } else {
    try {
      yield call(requestPostNotificationsPermissions)
      const result = yield call(checkPostNotificationsState)

      granted = result === PermissionState.GRANTED
    } catch (e) {
      logger.error('Permission request failed', {}, e)
      // Early return lets the notification screen reappear on next launch
      return
    }

    if (granted) {
      // On Android, this doesn't actually request permissions but calls getToken
      // which on success envokes onRegistration "forcing" a registration event
      PushNotification.requestPermissions()

      // On Android 14+, USE_FULL_SCREEN_INTENT is a runtime-controlled permission
      // required for incoming video call notifications to display as full-screen overlays.
      // There is no system dialog for this — if not already granted, open the dedicated
      // settings page so the user can enable it. Silently proceed either way.
      try {
        if (meetsMinimumVersion(ANDROID_API_VERSION.ANDROID_14)) {
          const canUseFullScreen = yield call(checkCanUseFullScreenIntent)
          if (!canUseFullScreen) {
            yield call(openSettings, 'fullscreen')
          }
        }
      } catch (e) {
        // Swallow any errors so that login/onboarding can continue normally.
        logger.error('Error checking or requesting full-screen intent permission', {}, e)
      }
    }
  }

  // Compromise: always enable SMS when the user grants notification permission.
  if (granted) {
    const sms = buildSMSPreference(true, true)
    yield put(NotificationActions.updateNotificationPreference(sms))
  }

  // Persist the response and navigate to the next step.
  yield put(StartupActions.setNotificationPermissionAccepted(granted))

  // When logging in or creating a new account the voip token will be set before
  // the user exists so try to register again here.
  if (isIOS()) {
    const { pushVoipToken } = yield select(appState)
    if (pushVoipToken) {
      yield put(StartupActions.setPushVoipTokenRequest(pushVoipToken))
    }
  }
}

// Show public computer page For web only
export function* navigateToPublicComputer() {
  const { deviceId, publicComputer } = yield select(loginState)

  if (isMobile() || publicComputer !== PublicComputerType.NO_ANSWER) {
    yield call(navigateWithPermission)
  } else {
    const { userInfo } = yield select(patientState)
    const devices = userInfo?.user_metadata?.devices
    const foundDevice = devices?.find((device) => device.device_id === deviceId)

    if (foundDevice) {
      //The user previously selected "dont ask me again on this computer" on this device,
      // update the store with the saved value
      yield put(
        LoginActions.submitPublicComputer({
          publicComputer: foundDevice.is_public
            ? PublicComputerType.ANSWERED_YES
            : PublicComputerType.ANSWERED_NO,
        })
      )
      yield call(navigateWithPermission)
    } else {
      // If the user is logging in and didn't say to not ask again on this device,
      // we show the PublicComputer screen again
      yield call(Nav.reset, { index: 0, routes: [{ name: 'publicComputer' }] })
    }
  }
}

// Org enforced MFA check and request
export function* navigateWithPermission() {
  const patient = yield select(patientState)

  const isMfaRequired = patient?.profile?.mfaRequired ?? false
  const isMfaEnabled = patient?.userInfo?.user_metadata?.mfa_enabled ?? false

  // User is required to have MFA enabled but it is not yet enabled
  if (isMfaRequired && !isMfaEnabled) {
    yield spawn(Analytics.trackLoadingTime, {
      action: LAUNCH_CATEGORY.EXIT,
      label: 'Mandatory MFA enforced',
    })
    yield call(Nav.reset, { index: 0, routes: [{ name: 'mandatoryMFAScreen' }] })
  } else {
    yield spawn(Analytics.trackLoadingTime, {
      action: 'Mandatory MFA check passed',
    })
    yield call(navigateWithMandatoryMfa)
  }
}

export function* navigateWithMandatoryMfa() {
  const needsAccountLinking = yield select(shouldShowAccountLinking)
  if (isEmbeddedEnvironment() && needsAccountLinking) return

  // Refresh brand eligibilities once the login flow has completed and if the user doesn't have any eligibility records
  const patient = yield select(patientState)

  if (!hasActiveEligibility(patient)) {
    yield call(ensureBrandEligibility)
  }
  yield all([call(fetchScribeV2User), call(fetchScribeV2UserEligibilities)])

  const isEmbedded = isEmbeddedEnvironment()
  let deeplink
  if (isEmbedded) {
    deeplink = yield select(deeplinkState)
  }

  if (!isEmbedded || !deeplink?.path) {
    // Standalone keeps the original reset flow. Embedded only skips this reset
    // when a deferred deeplink will replace it with the target route.
    // Redirect to the today screen
    yield call(NavSagas.syncReset, { index: 0, routes: [{ name: 'tabbar' }] })
  }

  // Onboarding is done, we can follow any deeplinks now
  yield put(PatientActions.setIsOnboarding(false))
  yield put(DeeplinkActions.setReadyToProcess(true))
  // Wait till the end of startup to cleanup the URL
  clearAllQueryParamsAndHash()

  // The connection has been consumed by a successful login. Clear it here so a silent SSO that
  // succeeds without a manual Login click (where WelcomeScreen never clears it) can't leave a
  // stale value that re-seeds manualLoginConnection on a later mount.
  clearPendingManualConnection()

  // Seamless SSO deeplink secondary action processing. The seam returns the destination
  // stashed before the auth step (sessionStorage on web, in-memory on native) and clears it.
  const pendingDestination = consumeDestination()
  if (pendingDestination) {
    // The seamless_sso_success funnel end is emitted at the auth completion point per platform
    // (web: token-hash return in StartupSagas; native: in-place in AuthSagas.login), not here —
    // this navigation path is only one of several that can run after a seamless login, so
    // emitting here would undercount.
    yield put(
      DeeplinkActions.setDeferredDeeplink(pendingDestination.path, pendingDestination.props)
    )
    yield put(DeeplinkActions.processDeferredDeeplink())
    yield spawn(Analytics.trackLoadingTime, { action: 'process deferred deeplink' })
    return
  }

  if (!isEmbedded) {
    deeplink = yield select(deeplinkState)
  }
  if (deeplink.path) {
    // Multi-step-deeplink: set deferredDeeplinkPath if second_action is present
    if (deeplink.props?.second_action) {
      yield put(DeeplinkActions.setDeferredDeeplink(deeplink.props.second_action, deeplink.props))
    }
    yield put(DeeplinkActions.processDeferredDeeplink())
    yield spawn(Analytics.trackLoadingTime, {
      action: 'process deferred deeplink',
    })
  }
}

export function* updateBasicProfile(action) {
  const login = yield select(loginState)
  const { props } = yield select(deeplinkState)
  const coredataClient = CoreData.create(login.accessToken)
  const updateResponse = yield call(coredataClient.updatePatient, action.patient)
  if (updateResponse.ok) {
    yield call(requestMessagingCredentials)
    yield spawn(requestEpisodes)
    yield put(PatientActions.patientProfileUpdateSuccess(updateResponse.data.data))
    yield call(trackDdEvent, REGISTRATION_ACTIONS.profileSubmitted)
    if (props?.code && Regex.enrolmentCode.test(props?.code)) {
      // call enrolment flow if enrolmentCode exist as we don't want to show claim access screen.
      yield call(enrolWithEnrolmentCodeDeeplink, props?.code)
      yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
    } else {
      yield call(navigateWithBasicProfile)
    }
  } else {
    yield put(PatientActions.patientProfileUpdateFailure(updateResponse))
  }
}

export function* getPillwayAccessToken() {
  try {
    const api = yield call(getEpharmacyApi)
    const patient = yield select(patientState)
    const authenticationRequest = {
      vendor: 'pillway',
    }
    const response = yield call(
      [api, api.epharmacyAuthenticateUser],
      patient.profile.id,
      authenticationRequest
    )
    const accessToken = response.data?.data?.token
    yield put(PatientActions.pillwayAuthenticationSuccess(accessToken))
  } catch (error) {
    yield put(PatientActions.pillwayAuthenticationFailure(error))
  }
}

export function* fetchServicesAdditionalData(services) {
  const pharmacyExternalIds = services
    ?.filter((service) => service.type === PharmacyType.epharmacy)
    ?.map((service) => service?.attributes?.epharmacyIdentifier)
    ?.join(',')

  if (pharmacyExternalIds) {
    yield put(pharmaciesActions.getPharmacies({ externalIds: pharmacyExternalIds }))
  }
}

export function* updateProfile(action) {
  const login = yield select(loginState)
  const coredataClient = CoreData.create(login.accessToken)
  const updateResponse = yield call(coredataClient.updatePatient, action.patient)
  if (updateResponse.ok) {
    yield put(PatientActions.patientProfileUpdateSuccess(updateResponse.data.data))
    if (action.type === 'PATIENT_PROFILE_UPDATE_REQUEST') {
      yield call(NestedNavHelper.pop)

      yield call(registerTokens)
    }
  } else {
    yield put(PatientActions.patientProfileUpdateFailure(updateResponse))
  }
}

export function* updateIdCard(action) {
  const login = yield select(loginState)
  const coredataClient = CoreData.create(login.accessToken)
  const response = yield call(coredataClient.getPatientMedicalIdUploadURL)
  if (response.ok) {
    const uploadResponse = yield call(
      coredataClient.uploadPatientMedicalID,
      response.data.data,
      action.fileObj
    )
    if (uploadResponse.ok) {
      const medicalIDResponse = yield call(coredataClient.getPatientMedicalIdURL)
      // overwrite the local representation of the profile
      if (medicalIDResponse.ok) {
        yield put(PatientActions.patientIdCardUpdateSuccess(medicalIDResponse.data.data.url))
      } else {
        yield put(PatientActions.patientIdCardUpdateFailure(medicalIDResponse))
      }
    } else {
      yield put(PatientActions.patientIdCardUpdateFailure(uploadResponse))
    }
  } else {
    yield put(PatientActions.patientIdCardUpdateFailure(response))
  }
}

export function* fetchScribeV2User() {
  yield put(PatientActions.scribeV2UserFetchRequest())

  const login = yield select(loginState)
  const patient = yield select(patientState)
  const scribe = Scribe.create(login)

  try {
    // i.e CA-QC
    const adminAreaISOCode = patient?.profile?.adminAreaISOCode
    const adminAreaLevel1 = adminAreaISOCode && adminAreaISOCode.split('-')[1]

    const data = yield call(scribe.getScribeV2User, patient.profile.id, {
      ...(adminAreaLevel1 && { home_province: adminAreaLevel1 }),
    })
    if (data === null || data === undefined) {
      throw new Error('No data returned')
    }
    yield put(PatientActions.scribeV2UserFetchSuccess(data))
    yield put(PatientActions.patientCreditCardFetchSuccess(data.creditCard))
    yield spawn(fetchServicesAdditionalData, data.healthResources)
  } catch (_) {
    yield put(PatientActions.scribeV2UserFetchFailure())
  }
}

export function* fetchScribeV2UserEligibilities() {
  yield put(PatientActions.scribeV2UserEligibilitiesFetchRequest())
  const login = yield select(loginState)
  const patient = yield select(patientState)
  const scribe = Scribe.create(login)

  try {
    const data = yield call(scribe.getScribeV2UserEligibilities, patient.profile.id)
    if (data === null || data === undefined) {
      throw new Error('No data returned')
    }
    yield put(PatientActions.scribeV2UserEligibilitiesFetchSuccess(data))
  } catch (_) {
    yield put(PatientActions.scribeV2UserEligibilitiesFetchFailure())
  }
}

export function* registerCreditCard(action) {
  const login = yield select(loginState)
  const patient = yield select(patientState)
  const scribe = Scribe.create(login)
  const token = action.token
  const chargeId = action.chargeId

  try {
    const scribeData = yield call(scribe.registerCreditCard, patient.profile.id, token)
    yield put(PatientActions.patientCreditCardRegisterSuccess(scribeData))
    if (!chargeId) {
      Alert.alert(
        I18n.t('PaymentScreen.add.success.title'),
        I18n.t('PaymentScreen.add.success.body'),
        [
          {
            text: I18n.t('PaymentScreen.add.success.accept'),
            onPress: () => {
              NestedNavHelper.pop()
            },
          },
        ]
      )
    }
  } catch (error) {
    yield put(PatientActions.patientCreditCardRegisterFailure(error))
    yield call(handleNonRetryableRequestFailure, I18n.t('error.alert.addCreditCardSaga'))
  }
}

export function* chargeCreditCard(action) {
  yield put(PatientActions.patientCreditCardChargeRequest())
  const login = yield select(loginState)
  const scribe = Scribe.create(login)
  const { chargeId } = action.args
  try {
    const res = yield call(scribe.chargeCreditCard, chargeId)
    yield put(PatientActions.patientCreditCardChargeSuccess(res))
    Alert.alert(
      I18n.t('PaymentScreen.alerts.success.title'),
      I18n.t('PaymentScreen.alerts.success.body', {
        amount: res.amount,
        description: res.description,
      }),
      [{ text: I18n.t('PaymentScreen.alerts.success.accept') }]
    )
  } catch (error) {
    // Differentiate between API errors and other exceptions
    yield put(PatientActions.patientCreditCardChargeFailure(error))

    if (error.status === '400') {
      if (error.code === 'user_not_registered_on_stripe') {
        Alert.alert(
          I18n.t('PaymentScreen.alerts.addCard.title'),
          I18n.t('PaymentScreen.alerts.addCard.body'),
          [
            { text: I18n.t('PaymentScreen.alerts.addCard.decline') },
            {
              text: I18n.t('PaymentScreen.alerts.addCard.accept'),
              onPress: () => Nav.navigate('addPayment', { chargeId }),
            },
          ]
        )
      }
      if (error.code === 'charge_not_pending') {
        const chargeStatus = error.source?.attributes?.status

        let key
        switch (chargeStatus) {
          case 'paid':
            key = 'alreadyPaid'
            break
          case 'failed':
            key = 'chargeFailed'
            break
          default:
            key = 'failure'
        }

        Alert.alert(
          I18n.t(`PaymentScreen.alerts.${key}.title`),
          I18n.t(`PaymentScreen.alerts.${key}.body`),
          [{ text: I18n.t(`PaymentScreen.alerts.${key}.accept`) }]
        )
      }
      if (error.code === 'capturing_stripe_charge_card_error') {
        /* istanbul ignore next */
        Alert.alert(
          I18n.t('PaymentScreen.alerts.failure.title'),
          I18n.t('PaymentScreen.alerts.failure.body'),
          [{ text: I18n.t('PaymentScreen.alerts.failure.accept') }]
        )
      }
    } else {
      Alert.alert(
        I18n.t('PaymentScreen.alerts.failure.title'),
        I18n.t('PaymentScreen.alerts.failure.body'),
        [{ text: I18n.t('PaymentScreen.alerts.failure.accept') }]
      )
    }
  }
}

export function* changeEmail(action) {
  try {
    const accessToken = yield select(selectAccessToken)
    const silkroad = Silkroad.create(accessToken)

    const { email, resend } = action
    if (email) {
      yield call(silkroad.changeEmailIntent, { new_email: email, use_deeplink: true })
      yield put(PatientActions.patientEmailChangeSuccess())

      if (resend) {
        yield delay(1) //delay due to alert not mixing well with full screen spinner changes.
        Alert.alert(
          I18n.t('ChangeEmailConfirmationScreen.alert.resent.title'),
          I18n.t('ChangeEmailConfirmationScreen.alert.resent.body'),
          [{ text: I18n.t('ChangeEmailConfirmationScreen.alert.resent.accept') }]
        )
      } else {
        yield call(Nav.navigate, 'changeEmailConfirmation', { email })
      }
    } else {
      yield put(
        PatientActions.patientEmailChangeFailure(I18n.t('Common.error.emailUpdate.invalid'))
      )
    }
  } catch (error) {
    const responseData = error.additionalInfo
    let errorMessage = I18n.t('Common.error.generic')
    if (responseData?.error_code) {
      switch (responseData.error_code) {
        case 'invalid_email':
          errorMessage = I18n.t('Common.error.emailUpdate.invalid')
          break
        case 'email_reuse':
          errorMessage = I18n.t('Common.error.emailUpdate.inUse')
          break
        case 'spam_block':
          errorMessage = I18n.t('ChangeEmailConfirmationScreen.alert.spamBlock.body')
          break
      }
    }

    const isSpamBlock = responseData?.error_code === 'spam_block'
    const title = isSpamBlock
      ? I18n.t('ChangeEmailConfirmationScreen.alert.spamBlock.title')
      : I18n.t('Common.error.emailUpdate.generic')
    let textObject = {
      text: I18n.t('Common.accept'),
    }
    if (isSpamBlock) {
      textObject = {
        text: I18n.t('ChangeEmailConfirmationScreen.alert.spamBlock.accept'),
        onPress: handlePressContactSupport,
      }
    }

    Alert.alert(title, errorMessage, [textObject])

    yield put(PatientActions.patientEmailChangeFailure(errorMessage))
  }
}

export function* ensureBrandEligibility() {
  const login = yield select(loginState)
  const patient = yield select(patientState)
  const scribe = Scribe.create(login)

  return yield call(scribe.ensureBrandEligibility, patient.profile.id, Config.BRAND_ID)
}

export function* enrolWithEnrolmentCode(code, brandId) {
  const login = yield select(loginState)
  const patient = yield select(patientState)
  const scribe = Scribe.create(login)

  try {
    return yield call(
      scribe.enrolWithEnrolmentCode,
      patient.profile.id,
      code,
      // No brand (or a null brand from Scribe) means the current brand
      // callers must check canEnrolInBrand first (look at enrolWithEnrolmentCodes)
      brandId ?? Config.BRAND_ID
    )
  } catch (_) {
    // Swallow the error so that the all call doesn't exit early
    return null
  }
}

export function* enrolWithEnrolmentCodes(
  data,
  options = { throwOnError: false, navigationOptions: {} }
) {
  const { throwOnError, navigationOptions } = options
  const patient = yield select(patientState)

  // If the user has enrolled at least one during onboarding, it should see the commitment screen
  if (patient?.isOnboarding) {
    yield put(PatientActions.setDisplayCommitmentScreen(true))
  }

  const { crossBrandEligibilityBrands } = yield select(selectFeatures)

  // Each entry is either an enrolment code (enrolled in the current brand) or { code, brandId }.
  // Entries for a brand the user can't be enrolled in from this app are skipped.
  const isEnrollable = (entry) =>
    typeof entry === 'string' || canEnrolInBrand(entry?.brandId, crossBrandEligibilityBrands)
  const toEnrolmentCall = (entry) =>
    typeof entry === 'string'
      ? call(enrolWithEnrolmentCode, entry)
      : call(enrolWithEnrolmentCode, entry?.code, entry?.brandId)

  const enrolmentCalls = (data?.codes || []).filter(isEnrollable).map(toEnrolmentCall)
  const results = (yield all(enrolmentCalls)).filter((result) => result)

  if (results.length > 0) {
    // Refresh the user, their eligibility intervals, and external claims (e.g. sunlife.access_id
    // required for ePharmacy tile visibility) if any enrolments were successful
    yield all([
      call(fetchScribeV2User),
      call(fetchScribeV2UserEligibilities),
      call(fetchMembersExternalClaims),
    ])
    yield put(PatientActions.patientEnrolWithEnrolmentCodesSuccess(results))
    yield call(trackDdEvent, REGISTRATION_ACTIONS.enrolledWithEnrolmentCode)

    const { challengedInvitationQueue } = yield select(patientState)

    if (challengedInvitationQueue?.length > 0) {
      yield put(PatientActions.challengedInvitationQueuePop())
    }

    const claimAccessSuccessParams = {
      ...(navigationOptions ?? {}),
      displayNames: results.map((result) => result.organization.displayName),
    }

    if (patient?.isOnboarding) {
      yield call(NestedNavHelper.reset, {
        index: 0,
        routes: [
          {
            name: 'claimAccessSuccessScreen',
            params: claimAccessSuccessParams,
          },
        ],
      })
    } else {
      yield call(NestedNavHelper.replace, 'claimAccessSuccessScreen', claimAccessSuccessParams)
    }

    // Refetch the app layout with the user's new contentTags
    yield call(fetchScribeV2User)
    const updatedPatient = yield select(patientState)
    yield spawn(contentSagas.fetchLayout, { contentTags: updatedPatient.profile.contentTags })

    //hack to login to active-minutes incase new eligibility was found
    yield spawn(activeMinutesLogin)
  } else {
    const error = new Error('No codes were accepted')
    yield put(PatientActions.patientEnrolWithEnrolmentCodesFailure(error))
    if (throwOnError ?? false) {
      throw error
    }
  }
}

export function* enrolWithEnrolmentCodeDeeplink(code) {
  logger.info('enrolWithEnrolmentCodeDeeplink saga started')
  yield call(waitIfOnVideoCallRoute, 'enrolWithEnrolmentCodeDeeplink')
  yield call(Nav.navigate, 'loading')
  try {
    yield call(enrolWithEnrolmentCodes, { codes: [code] }, { throwOnError: true })
  } catch (_) {
    const patient = yield select(patientState)
    const login = yield select(loginState)
    const scribe = Scribe.create(login)
    const canRetry = yield call(scribe.hasRemainingOrganizationSearchRetries)

    const { enableTrustBasedEligibilitySearch } = yield select(selectFeatures)

    if (enableTrustBasedEligibilitySearch && !hasActiveEligibility(patient) && canRetry) {
      return yield call(Nav.navigate, 'trustBasedEligibilitySearch')
    }

    yield call(Nav.navigate, 'eligibilityClaimScreen', { code })
  }
}

export function* fetchUserInfo(accessToken, forceRefresh = false) {
  yield put(PatientActions.userInfoFetchRequest())

  try {
    const res = yield call(authClient.getUserInfo, { accessToken, forceRefresh })
    yield put(PatientActions.userInfoFetchSuccess(res))
    return { ok: true, userInfo: res }
  } catch (error) {
    logger.error('Error fetching user info', {}, error)
    yield put(PatientActions.userInfoFetchFailure(error))
    return { ok: false, error }
  }
}

export function* unchallengedInvitationSearch() {
  const login = yield select(loginState)
  const profile = yield select(basicProfileState)
  const scribe = Scribe.create(login)
  const { crossBrandEligibilityBrands } = yield select(selectFeatures)
  const isCrossBrand = isCrossBrandListed(Config.BRAND_ID, crossBrandEligibilityBrands)

  try {
    const data = yield call(
      scribe.unchallengedInvitationSearch,
      profile.givenName,
      profile.familyName,
      profile.dateOfBirth,
      // Cross-brand brands search without a brand to get invitations from every brand.
      // Scribe rejects a search without brand_id for any other brand, so it must be sent.
      isCrossBrand ? undefined : Config.BRAND_ID
    )

    if (data === undefined || data.length === 0) {
      throw new Error('No invitations found')
    } else {
      // Invitations for brands the user can't be enrolled in are skipped by enrolWithEnrolmentCodes
      const codes = data.map((invitation) => ({
        code: invitation.enrolment_code,
        brandId: invitation.brand_id,
      }))
      yield call(enrolWithEnrolmentCodes, { codes }, { throwOnError: true })
      yield put(PatientActions.unchallengedInvitationSearchSuccess())
      yield call(trackDdEvent, REGISTRATION_ACTIONS.unchallengedEnrolmentSucceeded)
    }
  } catch (_) {
    yield put(PatientActions.unchallengedInvitationSearchFailure())
    yield call(trackDdEvent, REGISTRATION_ACTIONS.unchallengedEnrolmentFailed)
    yield call(challengedInvitationSearch)
  }
}

export function* challengedInvitationSearch() {
  yield put(PatientActions.challengedInvitationSearchRequest())
  const login = yield select(loginState)
  const profile = yield select(basicProfileState)
  const scribe = Scribe.create(login)
  const { crossBrandEligibilityBrands } = yield select(selectFeatures)
  const isCrossBrand = isCrossBrandListed(Config.BRAND_ID, crossBrandEligibilityBrands)

  try {
    const data = yield call(
      scribe.challengedInvitationSearch,
      profile.givenName,
      profile.familyName,
      profile.dateOfBirth,
      // Cross-brand brands search without a brand to get invitations from every brand.
      isCrossBrand ? undefined : Config.BRAND_ID
    )

    // Only offer challenges the user can complete: invitations for brands they can't be
    // enrolled in from this app would fail after the challenge is answered
    const isEnrollable = (invitation) =>
      canEnrolInBrand(invitation?.organization?.brandId, crossBrandEligibilityBrands)
    const invitations = Array.isArray(data) ? data : []
    const enrollableList = invitations.filter(isEnrollable)

    // Sadly, we do not have a create time property - but the ID is a number, incremented
    // We can safely use it to find the most recent one
    const byNewestFirst = (a, b) => b.id - a.id
    // Newest first, so the dedupe below keeps the latest invitation per organization
    const enrollableNewestFirst = [...enrollableList].sort(byNewestFirst)
    // Ensure there is only one challenge per organization
    const deduped = enrollableNewestFirst.reduce((result, invitation) => {
      const isOrganizationInResult = result.some(
        (i) => i?.organization?.id === invitation?.organization?.id
      )

      if (!isOrganizationInResult) {
        result.push(invitation)
      }
      return result
    }, [])

    if (deduped.length === 0) {
      throw new Error('No invitations found')
    }

    yield put(PatientActions.challengedInvitationSearchSuccess())
    yield call(Nav.navigate, 'challengedEnrolmentsSearchScreen', { searchResult: deduped })
  } catch (_) {
    yield put(PatientActions.challengedInvitationSearchFailure())
    const patient = yield select(patientState)
    const isOnboarding = patient?.isOnboarding

    // For C+, if user has no invitations AND during onboarding, skip to geolocation screen. Otherwise, show an error.
    // For other brands, show claim access screen.
    if (Config.SUPPORTED_CHALLENGED_ENROLMENT_METHOD == null) {
      if (!isOnboarding) {
        yield call(Nav.navigate, 'infoWarning_noInvitationsFound')
      }
    } else {
      const canRetry = yield call(scribe.hasRemainingOrganizationSearchRetries)

      const { enableTrustBasedEligibilitySearch } = yield select(selectFeatures)
      const eligibilityPath =
        enableTrustBasedEligibilitySearch && !hasActiveEligibility(patient) && canRetry
          ? 'trustBasedEligibilitySearch'
          : 'eligibilityClaimScreen'

      if (isOnboarding) {
        yield call(Nav.reset, { index: 0, routes: [{ name: eligibilityPath }] })
      } else {
        yield call(Nav.navigate, eligibilityPath)
      }
    }
  }
}

export function* challengedInvitationQueueNext() {
  const { challengedInvitationQueue } = yield select(patientState)
  const next = challengedInvitationQueue && challengedInvitationQueue[0]

  if (next) {
    if (next.id === 'claim_access') {
      yield call(Nav.navigate, 'eligibilityClaimScreen')
    } else if (next.challengeForm?.fields?.length > 0) {
      yield call(NestedNavHelper.replace, 'challengedEnrolmentUniqueId', {
        challengedInvitation: next,
      })
    } else {
      yield call(Nav.navigate, 'invitationEmail')
    }
  } else {
    // Queue is empty.
    // If member is in the middle of onboarding, go to the next step which is geolocation check. Otherwise, return home.
    const patient = yield select(patientState)
    if (patient.isOnboarding) {
      yield call(navigateToGeolocationCheck)
    } else {
      yield call(Nav.reset, { index: 0, routes: [{ name: 'tabbar' }] })
    }
  }
}

export function* challengeInvitation({ challenge, uniqueIdentifiers }) {
  const login = yield select(loginState)
  const scribe = Scribe.create(login)
  try {
    // Transform each individual field
    const transformedFields = uniqueIdentifiers.map((identifier, i) => {
      return EnrolmentChallengeOperations.transform(
        identifier.trim(),
        challenge?.challengeForm?.fields?.[i]?.operation
      )
    })

    // Join the fields
    // For single ID just take the first field (should be the only one) since fieldsOperation is undefined
    const transformedField = challenge?.challengeForm?.fieldsOperation
      ? EnrolmentChallengeOperations.transform(
          transformedFields,
          challenge?.challengeForm?.fieldsOperation
        )
      : transformedFields[0]
    const challengeAttribute = challenge.challengeForm.challengeAttribute

    const attribute = { [challengeAttribute]: transformedField }

    const data = yield call(scribe.challengeInvitation, challenge.id, attribute)
    if (!data?.enrolment_code) {
      throw new Error('No enrolment code in response')
    }

    // Enrol in the invitation's brand, which can be a partner brand for cross-brand brands
    const brandId = data.brand_id ?? challenge?.organization?.brandId
    const enrolmentEntry = { code: data.enrolment_code, brandId }
    yield call(enrolWithEnrolmentCodes, { codes: [enrolmentEntry] }, { throwOnError: true })
    yield put(PatientActions.challengeInvitationSuccess())
    yield call(trackDdEvent, REGISTRATION_ACTIONS.challengedEnrolmentSucceeded)
  } catch (error) {
    yield put(PatientActions.challengeInvitationFailure(error))
    yield call(trackDdEvent, REGISTRATION_ACTIONS.challengedEnrolmentFailed)
  }
}

export function* resendInvitation(data) {
  const login = yield select(loginState)
  const scribe = Scribe.create(login)
  const email = data?.email
  const brandId = Config.BRAND_ID

  try {
    yield call(scribe.resendInvitation, email, brandId)
    yield put(PatientActions.resendInvitationSuccess())

    yield call(Nav.navigate, 'checkEmail', { email })
  } catch (error) {
    yield put(PatientActions.resendInvitationFailure(error))
  }
}

export function* changeEmailAuthorized(token, email) {
  const accessToken = yield select(selectAccessToken)

  try {
    const silkroad = Silkroad.create(accessToken)
    yield call(silkroad.changeEmail, token)
    yield put(DeeplinkActions.processDeferredDeeplinkSuccess())
    yield call(Nav.navigate, 'verifyNewEmailResult', { success: true, email })
  } catch (error) {
    yield put(PatientActions.changeEmailFailure(error))
    yield call(Nav.navigate, 'verifyNewEmailResult', {
      errorType: VerifyEmailErrors.EMAIL_CHANGE_ERROR,
    })
    // Do not log if user enters wrong password
    if (error?.code !== 403) {
      logger.error('Failed to change email', {}, error)
    }
  }
}

export function* authorizeAndChangeEmail({ password }) {
  const { changeEmailToken } = yield select(patientState)
  const { new_email, old_email, token } = changeEmailToken
  const { accessToken } = yield select(loginState)
  yield put(PatientActions.setChangeEmailToken({ new_email, token, password }))
  if (accessToken) {
    yield call(changeEmailAuthorized, token, new_email)
  } else {
    try {
      yield call(
        loginWithUsernameAndPassword,
        {
          username: old_email,
          password,
        },
        true
      )
      yield call(changeEmailAuthorized, token, new_email)
    } catch (error) {
      yield put(PatientActions.changeEmailFailure(error))
      logger.error('Failed to authorize and change email', {}, error)
      yield put(LoginActions.logout(error.message))
    }
  }
}

export function* runStartupAfterChangeEmail() {
  const {
    changeEmailToken: { new_email },
  } = yield select(patientState)
  yield put(PatientActions.changeEmailSuccess(new_email))
  yield put(LoginActions.logout('Completed email change', true))
  yield call(Nav.reset, { index: 0, routes: [{ name: 'welcome' }] })
}

export function* processFamilyInvite(familyId) {
  try {
    yield call(renewAuth)
    yield call(familySagas.acceptFamilyInvite, { payload: { familyId } })
    yield all([call(fetchScribeV2User), call(fetchScribeV2UserEligibilities)])
  } catch (error) {
    logger.error('Error accepting family invite', {}, error)
  }
}
